'use client';

import { useEffect } from 'react';
import { createBrowserClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { captureMessage, captureError, buildTags, toSerializable } from '@/lib/monitoring/logger';
import { hasAnalyticsConsent, identifyUser, resetUser, setTenantGroup } from '@/lib/analytics';

const SESSION_ENDPOINT = '/auth/session';
const COMPANIES_CURRENT_ENDPOINT = '/api/companies/current';

interface CompanyCurrentResponse {
  company?: {
    id: string;
    name: string;
    slug: string;
  };
  role?: 'owner' | 'admin' | 'member' | 'viewer';
}

export function SupabaseSessionListener(): null {
  const router = useRouter();

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let removeConsentListener: (() => void) | null = null;

    /**
     * Fetch tenant context and set up PostHog user/group identification.
     * Non-blocking: analytics failures should not affect auth flow.
     */
    async function setupAnalyticsIdentity(userId: string, email?: string) {
      try {
        const response = await fetch(COMPANIES_CURRENT_ENDPOINT, {
          method: 'GET',
          credentials: 'same-origin',
        });

        if (!response.ok) {
          // User may not have a company yet (onboarding)
          identifyUser(userId, { email });
          return;
        }

        const data: CompanyCurrentResponse = await response.json();
        
        // Identify user with tenant context
        identifyUser(userId, {
          email,
          role: data.role,
          tenant_id: data.company?.id,
          tenant_name: data.company?.name,
        });

        // Set up group analytics for the tenant
        if (data.company?.id) {
          setTenantGroup(data.company.id, {
            name: data.company.name,
          });
        }
      } catch (error) {
        // Non-blocking: log but don't fail auth flow
        void captureError(error, {
          tags: buildTags('supabase-session-listener', 'analytics-identity'),
          extra: toSerializable({ userId })
        });
        // Still identify user without tenant context
        identifyUser(userId, { email });
      }
    }

    try {
      const supabase = createBrowserClient();

      const res = supabase.auth.onAuthStateChange(async (event, session) => {
        try {
          await fetch(SESSION_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ event, session }),
          });
        } catch (error) {
          void captureMessage('supabase-session.sync_failed', 'warning', {
            tags: buildTags('supabase-session-listener', 'sync'),
            extra: toSerializable({ event, error: error instanceof Error ? error.message : String(error) })
          });
        }

        // PostHog user identification on sign-in
        if (event === 'SIGNED_IN' && session?.user) {
          void setupAnalyticsIdentity(session.user.id, session.user.email);
        }

        // Reset PostHog identity on sign-out
        if (event === 'SIGNED_OUT') {
          resetUser();
          router.push('/auth/login');
        }
      });

      subscription = res.data.subscription;

      const handleConsentUpdated = () => {
        if (!hasAnalyticsConsent()) return;

        void supabase.auth.getUser()
          .then(({ data }) => {
            if (!data.user) return;
            void setupAnalyticsIdentity(data.user.id, data.user.email);
          })
          .catch((error) => {
            void captureError(error, {
              tags: buildTags('supabase-session-listener', 'analytics-consent-replay'),
              extra: toSerializable({})
            });
          });
      };

      window.addEventListener('consent-updated', handleConsentUpdated);
      removeConsentListener = () => window.removeEventListener('consent-updated', handleConsentUpdated);
    } catch (error) {
      // Swallow client init errors (e.g., NavigatorLockAcquireTimeoutError) to avoid blocking UI render
      void captureMessage('supabase-session.init_failed', 'warning', {
        tags: buildTags('supabase-session-listener', 'init'),
        extra: toSerializable({ error: error instanceof Error ? error.message : String(error) })
      });
    }

    return () => {
      removeConsentListener?.();
      try {
        subscription?.unsubscribe();
      } catch {
        // noop
      }
    };
  }, [router]);

  return null;
}

export default SupabaseSessionListener;
